"use client";

import React, { useState } from 'react';
import {
  Shield, Phone, Mail, CheckCircle, XCircle, Loader2,
  ArrowRight, RefreshCw
} from 'lucide-react';
import { getWeaveinApiUrl } from '@/src/config';
import { useTabbedChrome } from '@/components/weavein';

type VerificationType = 'phone' | 'email_domain';
type Step = 'select' | 'initiate' | 'confirm' | 'done';

const headers = (): Record<string, string> => {
  const cboId = localStorage.getItem('active_cbo_id');
  return {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${localStorage.getItem('auth_token')}`,
    ...(cboId && { 'X-CBO-ID': cboId }),
  };
};

export const SenderVerificationWizard: React.FC = () => {
  const tabbed = useTabbedChrome();
  const organizationId = localStorage.getItem('active_cbo_id') ? Number(localStorage.getItem('active_cbo_id')) : null;
  const [type, setType] = useState<VerificationType>('phone');
  const [step, setStep] = useState<Step>('select');
  const [value, setValue] = useState('');
  const [code, setCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<any>(null);
  const [dnsRecords, setDnsRecords] = useState<any[]>([]);

  const reset = () => {
    setStep('select');
    setValue('');
    setCode('');
    setError(null);
    setResult(null);
    setDnsRecords([]);
  };

  const initiatePhoneVerification = async () => {
    if (!organizationId || !value) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/communication-providers/verify-phone`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ organization_id: organizationId, phone_number: value }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to send verification code');
      setResult(data);
      setStep('confirm');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const confirmPhoneVerification = async () => {
    if (!organizationId || !value || !code) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/communication-providers/verify-phone/confirm`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ organization_id: organizationId, phone_number: value, code }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Verification failed');
      setResult(data);
      setStep('done');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const initiateEmailDomainVerification = async () => {
    if (!organizationId || !value) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(`${getWeaveinApiUrl()}/api/communication-providers/verify-email-domain`, {
        method: 'POST',
        headers: headers(),
        body: JSON.stringify({ organization_id: organizationId, domain: value }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to initiate verification');
      setDnsRecords(data.dns_records || []);
      setResult(data);
      setStep('confirm');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const checkEmailDomainVerification = async () => {
    if (!organizationId || !value) return;
    setLoading(true);
    setError(null);
    try {
      const res = await fetch(
        `${getWeaveinApiUrl()}/api/communication-providers/verify-email-domain/${value}/status?organization_id=${organizationId}`,
        { headers: headers() }
      );
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Check failed');
      setResult(data);
      if (data.status === 'verified') setStep('done');
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        {!tabbed && (
          <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
            <Shield className="w-5 h-5 text-blue-600" />
            Sender Verification
          </h2>
        )}
        <p className="text-sm text-gray-500 mt-1">
          Verify ownership of phone numbers and email domains before using them as senders
        </p>
      </div>

      {/* Step 1: Select verification type */}
      {step === 'select' && (
        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-4">
            <button
              onClick={() => setType('phone')}
              className={`p-6 border-2 rounded-xl text-left transition-all ${
                type === 'phone' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Phone className={`w-8 h-8 mb-3 ${type === 'phone' ? 'text-blue-600' : 'text-gray-400'}`} />
              <h3 className="font-semibold text-gray-900">Phone Number</h3>
              <p className="text-sm text-gray-500 mt-1">Verify via SMS code</p>
            </button>
            <button
              onClick={() => setType('email_domain')}
              className={`p-6 border-2 rounded-xl text-left transition-all ${
                type === 'email_domain' ? 'border-blue-500 bg-blue-50' : 'border-gray-200 hover:border-gray-300'
              }`}
            >
              <Mail className={`w-8 h-8 mb-3 ${type === 'email_domain' ? 'text-blue-600' : 'text-gray-400'}`} />
              <h3 className="font-semibold text-gray-900">Email Domain</h3>
              <p className="text-sm text-gray-500 mt-1">Verify via DNS records</p>
            </button>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              {type === 'phone' ? 'Phone Number' : 'Domain'}
            </label>
            <input
              type="text"
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={type === 'phone' ? '+15551234567' : 'yourorg.com'}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          <button
            onClick={type === 'phone' ? initiatePhoneVerification : initiateEmailDomainVerification}
            disabled={loading || !value}
            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <ArrowRight className="w-4 h-4" />}
            {type === 'phone' ? 'Send Verification Code' : 'Generate DNS Records'}
          </button>
        </div>
      )}

      {/* Step 2: Confirm */}
      {step === 'confirm' && type === 'phone' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            A verification code has been sent to <strong>{value}</strong>. Enter it below.
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Verification Code</label>
            <input
              type="text"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              placeholder="123456"
              maxLength={6}
              className="w-full px-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 text-center text-2xl tracking-widest"
            />
          </div>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          <div className="flex gap-3">
            <button onClick={reset} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Back</button>
            <button
              onClick={confirmPhoneVerification}
              disabled={loading || code.length < 4}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle className="w-4 h-4" />}
              Verify
            </button>
          </div>
        </div>
      )}

      {step === 'confirm' && type === 'email_domain' && (
        <div className="space-y-4">
          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 text-sm text-blue-800">
            Add these DNS records to <strong>{value}</strong>, then click &quot;Check Propagation&quot;.
          </div>

          {dnsRecords.length > 0 && (
            <div className="border rounded-lg overflow-hidden">
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Type</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Host</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Value</th>
                    <th className="text-left px-4 py-2 font-medium text-gray-600">Purpose</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {dnsRecords.map((r, i) => (
                    <tr key={i}>
                      <td className="px-4 py-2 font-mono text-xs">{r.type}</td>
                      <td className="px-4 py-2 font-mono text-xs">{r.host}</td>
                      <td className="px-4 py-2 font-mono text-xs break-all">{r.value}</td>
                      <td className="px-4 py-2 text-xs text-gray-500">{r.purpose}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result?.status === 'pending' && (
            <div className="text-sm text-yellow-600 flex items-center gap-2">
              <RefreshCw className="w-4 h-4" /> DNS records not yet propagated
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}

          <div className="flex gap-3">
            <button onClick={reset} className="px-4 py-2 text-sm border rounded-lg hover:bg-gray-50">Back</button>
            <button
              onClick={checkEmailDomainVerification}
              disabled={loading}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 text-sm font-medium"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
              Check Propagation
            </button>
          </div>
        </div>
      )}

      {/* Step 3: Done */}
      {step === 'done' && (
        <div className="text-center py-8">
          <CheckCircle className="w-16 h-16 mx-auto mb-4 text-green-500" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">Verification Complete</h3>
          <p className="text-sm text-gray-500 mb-6">
            {type === 'phone'
              ? `Phone number ${value} has been verified.`
              : `Email domain ${value} has been verified.`
            }
          </p>
          <button
            onClick={reset}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 text-sm font-medium"
          >
            Verify Another
          </button>
        </div>
      )}
    </div>
  );
};

export default SenderVerificationWizard;
